import type { UnknownRecord } from 'type-fest';
import type { ModelSummary } from '@/src/components/ListingCard/types';

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null;
}

function asNumber(value: unknown): number | undefined {
  if (typeof value !== 'number' && typeof value !== 'string') return undefined;
  if (typeof value === 'string' && value.trim().length === 0) return undefined;

  const numberValue = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numberValue) ? numberValue : undefined;
}

function assignNumber(summary: ModelSummary, key: keyof ModelSummary, value: unknown): void {
  const numberValue = asNumber(value);
  if (numberValue !== undefined) {
    summary[key] = numberValue;
  }
}

function compactSummary(summary: ModelSummary): ModelSummary | undefined {
  return Object.keys(summary).length > 0 ? summary : undefined;
}

function deriveFromPricingSummary(listing: UnknownRecord): ModelSummary | undefined {
  const pricing = listing.pricing;
  if (!isRecord(pricing) || !isRecord(pricing.summary)) return undefined;

  const rawSummary = pricing.summary;
  const summary: ModelSummary = {};
  assignNumber(summary, 'minBeds', rawSummary.bedsLow);
  assignNumber(summary, 'maxBeds', rawSummary.bedsHigh);
  assignNumber(summary, 'minBaths', rawSummary.bathsLow);
  assignNumber(summary, 'maxBaths', rawSummary.bathsHigh);
  assignNumber(summary, 'minSqft', rawSummary.sqFtLow);
  assignNumber(summary, 'maxSqft', rawSummary.sqFtHigh);
  assignNumber(summary, 'minPrice', rawSummary.priceLow);
  assignNumber(summary, 'maxPrice', rawSummary.priceHigh);
  return compactSummary(summary);
}

function rangeFromModels(models: Array<UnknownRecord>, key: string): { min: number; max: number } | undefined {
  const values = models
    .map((model) => asNumber(model[key]))
    .filter((value): value is number => value !== undefined);

  if (values.length === 0) return undefined;
  return {
    min: Math.min(...values),
    max: Math.max(...values),
  };
}

function deriveFromModels(
  listing: UnknownRecord,
  shouldIncludeModel?: (model: UnknownRecord) => boolean,
): ModelSummary | undefined {
  const rawModels = listing.models;
  if (!Array.isArray(rawModels)) return undefined;

  const models = rawModels.filter(isRecord).filter((model) => shouldIncludeModel?.(model) ?? true);
  if (models.length === 0) return undefined;

  const beds = rangeFromModels(models, 'beds');
  const baths = rangeFromModels(models, 'baths');
  const sqft = rangeFromModels(models, 'sqft');
  const lowPrice = rangeFromModels(models, 'low');
  const highPrice = rangeFromModels(models, 'high');
  const summary: ModelSummary = {};

  if (beds) {
    summary.minBeds = beds.min;
    summary.maxBeds = beds.max;
  }
  if (baths) {
    summary.minBaths = baths.min;
    summary.maxBaths = baths.max;
  }
  if (sqft) {
    summary.minSqft = sqft.min;
    summary.maxSqft = sqft.max;
  }
  if (lowPrice) {
    summary.minPrice = lowPrice.min;
  }
  if (highPrice) {
    summary.maxPrice = highPrice.max;
  }

  return compactSummary(summary);
}

export function deriveModelSummaryFromListingModels(
  listing: UnknownRecord,
  shouldIncludeModel?: (model: UnknownRecord) => boolean,
): ModelSummary | undefined {
  return deriveFromModels(listing, shouldIncludeModel);
}

function readExistingModelSummary(listing: UnknownRecord): ModelSummary | undefined {
  if (!isRecord(listing.modelSummary)) return undefined;

  const summary: ModelSummary = {};
  for (const key of Object.keys(listing.modelSummary) as Array<keyof ModelSummary>) {
    assignNumber(summary, key, listing.modelSummary[key]);
  }
  return compactSummary(summary);
}

export function deriveModelSummaryFromListing(listing: UnknownRecord): ModelSummary | undefined {
  const fromModels = deriveFromModels(listing);
  const fromPricing = deriveFromPricingSummary(listing);
  const existing = readExistingModelSummary(listing);

  return compactSummary({
    ...fromModels,
    ...fromPricing,
    ...existing,
  });
}
